// RegistroAccionesLider — botón de acción por fila para el LÍDER sobre un
// registro capturado.
//
// REGLA (fuente única de verdad en el backend, server clock):
//   editable = isOwner && registro.editable_por_lider
//     · isOwner = registro.lider_asignado_user_id === current user id
//     · editable_por_lider lo computa el backend en SQL: el bloque sigue dentro
//       de la ventana de edición = turno activo + gracia post-turno (ver
//       VENTANA_GRACIA_EDICION_MIN, ADR-0004). El FE NO recalcula tiempos.
//
//   · editable    → botón "Editar" → EditarHoraDialog con motivo (≥20) REQUERIDO
//   · no editable → sin botón (hint "No editable")
//
// Editar un bloque ya validado lo devuelve a pendiente (lo hace el backend).
// Fuera de la ventana, solo el supervisor edita. El server re-valida y responde
// 423 si el cliente intenta editar fuera de la ventana.

import { useState } from 'react';
import type { RegistroProduccionHora } from 'schemas';
import { Button } from '../../components/ui/button';
import { useTouchTargets } from '../../components/ui/touch-targets';
import { EditarHoraDialog } from './EditarHoraDialog';

/**
 * El bloque es editable por el líder si es dueño Y el backend marcó
 * `editable_por_lider` (dentro de la ventana de edición turno + gracia).
 */
export function esBloqueEditablePorLider(
  registro: RegistroProduccionHora,
  currentUserId: string,
): boolean {
  if (registro.estado === 'invalidado') return false;
  if (registro.lider_asignado_user_id !== currentUserId) return false;
  return registro.editable_por_lider === true;
}

export function RegistroAccionesLider({
  registro,
  currentUserId,
  /** Cuando se monta dentro de una fila clickable, frenar la propagación. */
  stopPropagation = true,
}: {
  registro: RegistroProduccionHora;
  currentUserId: string;
  stopPropagation?: boolean;
}) {
  const touch = useTouchTargets();
  const [editar, setEditar] = useState<RegistroProduccionHora | null>(null);

  const editable = esBloqueEditablePorLider(registro, currentUserId);

  function wrap(fn: () => void) {
    return (e: React.MouseEvent) => {
      if (stopPropagation) e.stopPropagation();
      fn();
    };
  }

  return (
    <>
      {editable ? (
        <Button
          type="button"
          size={touch ? 'touch' : 'sm'}
          variant="outline"
          onClick={wrap(() => setEditar(registro))}
        >
          Editar
        </Button>
      ) : registro.estado === 'invalidado' ? null : (
        <span className="text-xs text-fg-tertiary">No editable</span>
      )}
      <EditarHoraDialog
        registro={editar}
        requiereRazon
        hint="Indique el motivo del cambio (mínimo 20 caracteres). Editar vuelve el bloque a pendiente de validación."
        onClose={() => setEditar(null)}
      />
    </>
  );
}
